/**
 * Weight integrity for extracted evaluation criteria.
 *
 * A bid manager reads the criteria table to work out where the marks are. If
 * the weights do not add up, either the extraction missed a criterion or the
 * tender itself is inconsistent, and both are worth knowing before any effort
 * is allocated. This turns that arithmetic into one line of copy.
 */

import type { ExtractedCriterion } from '@/types/extraction'

export type CriteriaWeightTone = 'none' | 'confirm' | 'warn' | 'points'

export interface CriteriaWeightSummary {
  tone: CriteriaWeightTone
  /** Empty string when tone is 'none' — the caller renders nothing. */
  message: string
}

/**
 * How far off 100 a total may sit and still read as healthy.
 *
 * Tenders round: three criteria at 33.3% each state 99.9, and a table that
 * flagged that as a defect would cry wolf on perfectly ordinary documents.
 */
const TOLERANCE = 0.5

/** At most one decimal, with a trailing `.0` stripped. */
function fmt(value: number): string {
  const rounded = Math.round(value * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0)
}

/**
 * Root criteria only: sub-criteria weights are shares of their parent, and the
 * backend Overview roll-up sums the same way.
 *
 * A tender that states no weights is not a defect (plenty are unweighted), so
 * it says nothing rather than nagging. Points-scored tenders get the total as
 * a neutral fact: nothing in a tender ever declares an expected point total,
 * so there is no verdict to give.
 */
export function summariseCriteriaWeights(
  rootCriteria: ExtractedCriterion[],
): CriteriaWeightSummary {
  const percentageTotal = sum(rootCriteria.map((c) => c.weightPercentage ?? 0))

  if (percentageTotal > 0) {
    if (Math.abs(percentageTotal - 100) <= TOLERANCE) {
      // The literal 100%, never the rounded sum: echoing "99.9%" back at a
      // total we have just judged healthy reads as a defect to chase.
      return { tone: 'confirm', message: 'Weights total 100%' }
    }

    const shortfall =
      percentageTotal > 100
        ? `${fmt(percentageTotal - 100)}% over, a weight may be wrong.`
        : `${fmt(100 - percentageTotal)}% unaccounted, a criterion may be missing.`

    return {
      tone: 'warn',
      message: `Weights total ${fmt(percentageTotal)}%. ${shortfall}`,
    }
  }

  const pointsTotal = sum(rootCriteria.map((c) => c.weightPoints ?? 0))
  if (pointsTotal > 0) {
    return { tone: 'points', message: `Points total ${fmt(pointsTotal)} pts` }
  }

  return { tone: 'none', message: '' }
}
